import { useState } from 'react'
import { useTar } from '@/modules'
import { CardButton, InlineConfirm } from '@duck/ui'
import { ChallengeService } from '@duck/api-definitions'
import { PostgresIcon } from './icons/PostgresIcon'
import { PythonIcon } from './icons/PythonIcon'

interface ChallengePanelProps {
  allowedWeapons: ChallengeService.TaskWeaponsOfChoiceEnum[]
  chosenWeapon: ChallengeService.TaskWeaponsOfChoiceEnum
  onChosenWeaponChange: (value: ChallengeService.TaskWeaponsOfChoiceEnum) => void
}

export const ChallengePanel = ({
  allowedWeapons,
  chosenWeapon,
  onChosenWeaponChange,
}: ChallengePanelProps) => {
  const { t } = useTar()
  const [pendingWeapon, setPendingWeapon] =
    useState<ChallengeService.TaskWeaponsOfChoiceEnum | null>(null)

  const canPickSql = allowedWeapons.includes(ChallengeService.TaskWeaponsOfChoiceEnum.Sql)
  const canPickPython = allowedWeapons.includes(ChallengeService.TaskWeaponsOfChoiceEnum.Python)

  const previewWeapon = pendingWeapon ?? chosenWeapon
  const showConfirm = pendingWeapon !== null && pendingWeapon !== chosenWeapon

  const handlePick = (weapon: ChallengeService.TaskWeaponsOfChoiceEnum) => {
    if (weapon === chosenWeapon) {
      setPendingWeapon(null)
      return
    }
    setPendingWeapon(weapon)
  }

  const handleConfirm = () => {
    if (pendingWeapon === null) return
    onChosenWeaponChange(pendingWeapon)
    setPendingWeapon(null)
  }

  const handleCancel = () => {
    setPendingWeapon(null)
  }

  return (
    <div>
      <div className="mb-1 text-lg font-semibold text-foreground">
        {t('common.settingsThisChallenge')}
      </div>
      <div className="mb-5 text-xs text-muted-foreground">
        {t('common.settingsScopedToThisChallenge')}
      </div>

      <div className="mb-2 text-sm font-medium text-foreground">
        {t('common.settingsLanguage')}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {canPickSql && (
          <CardButton
            icon={<PostgresIcon className="h-10 w-10" />}
            title={t('common.settingsLanguageSql')}
            selected={previewWeapon === ChallengeService.TaskWeaponsOfChoiceEnum.Sql}
            onClick={() => handlePick(ChallengeService.TaskWeaponsOfChoiceEnum.Sql)}
          />
        )}
        {canPickPython && (
          <CardButton
            icon={<PythonIcon className="h-10 w-10" />}
            title={t('common.settingsLanguagePython')}
            selected={previewWeapon === ChallengeService.TaskWeaponsOfChoiceEnum.Python}
            onClick={() => handlePick(ChallengeService.TaskWeaponsOfChoiceEnum.Python)}
          />
        )}
      </div>

      {showConfirm && (
        <div className="mt-4">
          <InlineConfirm
            variant="warning"
            labels="confirmCancel"
            message={t('common.settingsLanguageSwitchWarning')}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        </div>
      )}
    </div>
  )
}
